<template>
  <!--
    TV-2188: shown between the price summary and the button when the customer's
    legacy balance would pay for this order in full once it is on Chargebee —
    the server decides that (GetBalanceMigrationOffer), for both checkout flows.
    A box rather than a modal that opens on its own, so the order flow is never
    interrupted; the modal opens only when the customer asks for it.
  -->
  <!-- border-gradient-rainbow inherits its radius, so the wrapper sets it. -->
  <div v-if="offer" class="rounded-xl">
    <!-- The accent gradient (#FFC75E → #F03DA2, --color-gradient-rainbow) the
         site uses for Loot Pass: it stands out from the summary card without
         competing with the cyan button below it. -->
    <button
      type="button"
      class="group border-gradient-rainbow flex w-full items-center gap-4 p-4 text-left transition-shadow hover:shadow-[0_0_24px_-6px_var(--gp-accent-magenta)]"
      @click="modalOpen = true"
    >
      <span
        class="flex size-11 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-accent-gold to-accent-magenta text-onyx shadow-lg"
      >
        <fa-icon :icon="faWallet" class="size-5" aria-hidden="true" />
      </span>
      <span class="flex-1">
        <i18n-t
          keypath="checkout/balance_migration/box/text"
          tag="span"
          class="block text-mouse"
        >
          <template #amount>
            <strong class="text-brand">{{ formattedOffer }}</strong>
          </template>
        </i18n-t>
        <span
          class="mt-1 flex items-center gap-1.5 text-sm font-bold uppercase text-brand"
        >
          {{ t('checkout/balance_migration/box/action') }}
          <fa-icon
            :icon="faChevronRight"
            class="size-3 transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </span>
      </span>
    </button>

    <checkout-balance-migration-modal
      :open="modalOpen"
      :amount="offer"
      @close="modalOpen = false"
      @migrated="onMigrated"
    />
  </div>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/nuxt';
import { faChevronRight, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { GetBalanceMigrationOfferRequest } from 'gportal-grpc/gportal/api/payment/v1/requests_pb';
import type { Money } from 'gportal-grpc/gportal/type/v1/money_pb';

const emit = defineEmits<{
  // The balance is on Chargebee now. Step 4 restarts its checkout on this, so
  // the balance arm appears.
  (e: 'migrated'): void;
}>();

const { t } = useI18n();
const { format: formatMoney } = useNumberMoneyFormatting();
const buildAuthMetadata = useAuthMetadata();
const authStore = useAuthStore();
const checkoutStore = useCheckoutStore();
const paymentStore = usePaymentStore();

const offer = ref<Money | null>(null);
const modalOpen = ref(false);

const formattedOffer = computed(() =>
  offer.value ? formatMoney(offer.value) : '',
);

// Only asked for a logged-in customer in the region the order is for: the
// offer is about the caller's own balance, in the region that would bill it.
const canAsk = computed(
  () =>
    authStore.isAuthenticated &&
    !checkoutStore.regionMismatch &&
    checkoutStore.price?.total != null,
);

let requestSeq = 0;

async function fetchOffer(): Promise<void> {
  const seq = ++requestSeq;
  // The customer's own balance: asked from the browser, never during SSR.
  if (import.meta.server || !canAsk.value) {
    offer.value = null;
    return;
  }

  const $grpcPayment = useGrpcPayment();
  const { response, error } = await $grpcPayment.executeApiCallActive(
    'getBalanceMigrationOffer',
    new GetBalanceMigrationOfferRequest({
      order: checkoutStore.toCreateCheckoutPaymentIntentRequest().order,
    }),
    buildAuthMetadata(),
  );
  // A newer configuration asked since; its answer is the one that counts.
  if (seq !== requestSeq) return;

  if (error != null) {
    // A hint, never a blocker: no box, and the checkout works as before.
    captureException(error, {
      extra: { context: 'BalanceMigrationBox.getBalanceMigrationOffer' },
    });
    offer.value = null;
    return;
  }
  offer.value = response?.offer ?? null;
}

// The offer depends on the order total, so a change of units, duration,
// location, add-ons or coupon asks again.
watch(
  () => [
    canAsk.value,
    checkoutStore.price?.total?.amount,
    checkoutStore.price?.total?.currencyCode,
    authStore.activeRegion,
  ],
  () => {
    void fetchOffer();
  },
  { immediate: true },
);

// Everything that read the old answer reads it again: the migrated flag and the
// session wallet. Asking for the offer again then hides the box.
async function onMigrated(): Promise<void> {
  modalOpen.value = false;
  paymentStore.invalidateBalanceMigrated();
  try {
    // A refresh that fails leaves a stale wallet, not a stale checkout: the
    // checkout is restarted regardless, and it reads the balance itself.
    await Promise.allSettled([
      paymentStore.fetchBalanceMigrated(),
      authStore.fetchCustomers({ preserveRegion: true }),
    ]);
    await fetchOffer();
  } finally {
    emit('migrated');
  }
}
</script>
